import {OidcMetadata, User, UserManager, UserManagerSettings} from 'oidc-client';
import {useGlobalStore} from "@/stores/GlobalStore";
import jwt_decode from "jwt-decode";
import {WebCallService} from "@/utilities/services/WebCallService";
import {$t} from "@/utilities/i18n/config";
import {notify} from "@kyvg/vue3-notification";
import {isDev} from "@/AppConfig";
import NullOrUndefinedHelper from "@/utilities/helpers/NullOrUndefinedHelper";

// helper class to not have multiple userManagerInstances across multiple OAuthServiceInstances
// pseudo singleton to avoid event-calling overflow
// which helps with token managment
// TODO needs to be reworked when SSO is implemented, since we have mutliple identityproviders in that case
class StaticUserManager {
    private static globalInstance: UserManager|null = null;

    public static getUserManager(settings: UserManagerSettings):UserManager {
        if(this.globalInstance) return this.globalInstance;

        this.globalInstance = new UserManager(settings);
        this.globalInstance.events.addAccessTokenExpiring(this.tokenExpiringEvent)
        this.globalInstance.events.addAccessTokenExpired(this.tokenExpiredEvent)

        return this.globalInstance;
    }

    // renew tokens when they are close to expireing (set by Setting 'accessTokenExpiringNotificationTime')
    private static async tokenExpiringEvent(){
        const instance = AuthService.getDefaultInstance()
        try {
            await instance.userManager.signinSilent() // refreshes tokens
        }
        catch (error:any) {
            if(error.message === "invalid_grant"){
                console.log("token invalidating soon")
            }
        }
    }

    // called when tokens are invalid due to expiration
    private static async tokenExpiredEvent(){
        await AuthService.getDefaultInstance().logoutSilent()
        notify({
            title: $t('general.token.expired.title'),
            text: $t('general.token.expired.text'),
            duration: -1,
            type: "error"
        });
    }
}

export default class AuthService {
    public userManager: UserManager;

    public static getDefaultInstance():AuthService {
        const globalStore = useGlobalStore();

        const authUrl = globalStore.settings.systemConfig!.authUrl;
        const clientId = globalStore.settings.systemConfig!.clientId;
        const scopes = globalStore.settings.systemConfig!.clientScopes;
        const callbackUrls = globalStore.getCallbackUrls();
        const ssoCallback = callbackUrls.normal;
        const ssoSilentCallback = callbackUrls.silent;

        return new AuthService(authUrl, clientId, scopes, ssoCallback, ssoSilentCallback);
    }

    constructor(authority:string, clientId:string, scopes:string, callback:string, silentCallback:string, metadata:OidcMetadata|undefined = undefined) {
        const settings: UserManagerSettings = {
            authority: authority,
            client_id: clientId,
            redirect_uri: window.location.origin + callback,
            silent_redirect_uri: window.location.origin + silentCallback,
            accessTokenExpiringNotificationTime: 10, //seconds
            response_type: 'code',
            scope: scopes,
            post_logout_redirect_uri: window.location.origin + '/logout',
            filterProtocolClaims: false,
            revokeAccessTokenOnSignout: true,
            metadata: metadata
        };

        this.userManager = StaticUserManager.getUserManager(settings);
    }

    public async login(redirectAfterLogin:string|null):Promise<void> {
        return NullOrUndefinedHelper(redirectAfterLogin)
            ? await this.userManager.signinRedirect()
            : await this.userManager.signinRedirect({
                state: { redirectAfterLogin }
            });
    }

    public async logoutSilent():Promise<void> {
        const user = await this.userManager.getUser();
        const id_token = user!.id_token
        const decodedToken = id_token ? jwt_decode(user!.id_token) as {iss:string, sid:string} : null;
        const issuer = decodedToken?.iss
        const state = decodedToken?.sid
        const url = `${issuer}/connect/endsession?id_token_hint=${id_token}&state=${state}&post_logout_redirect_uri=${window.location.origin}/logout`;

        try {
            // 1st revoke the access and refresh token
            await this.userManager.revokeAccessToken();
            if (decodedToken !== null) {
                // make 'silent' call WITH COOKIES to IDS to end the session for the user
                await new WebCallService().getFromExternalAPI(url, true);
            }
        }
        catch (error) {
            console.error("Error during silent logout:", error);
        }

        await this.userManager.removeUser();
        await this.userManager.clearStaleState()
    }

    public async loginSilent():Promise<User | undefined> {
        return await this.userManager.signinSilentCallback();
    }

    public async getAccessToken():Promise<string|null>{
        if(!isDev){
            try {
                const user = await this.userManager.getUser();
                if(NullOrUndefinedHelper(user)) return null;
                return user!.access_token ?? null;
            }
            catch (error) {
                console.log("Error getting access token:", error);
                return null;
            }
        }

        return "eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImtpZCI6Il9qTndqZVNudlRUSzhYRWRyNVFVUGtCUkxMbyJ9.eyJhdWQiOiJiMzY0YzU0Ny0xNWY2LTQ0YzgtYjQzNi01MjcyZmViMjQxN2EiLCJpc3MiOiJodHRwczovL2xvZ2luLm1pY3Jvc29mdG9ubGluZS5jb20vZGRkMDg5ZjMtYTEyNy00ODU4LTg2NzItYWU5MGJmODJkOTA0L3YyLjAiLCJpYXQiOjE3NTE4ODQxOTQsIm5iZiI6MTc1MTg4NDE5NCwiZXhwIjoxNzUxODg4MjEwLCJhaW8iOiJBWVFBZS84WkFBQUF6TWd5dWUrMVB4ZnZHNzlFcUh6S0FYRTl0VUljNDlmZEthMnE3SDdNK3hRV3FjS1pnaWg3VFpMdk5wRjhxUjJjMDhHdVk5UzlEbDFGWkFVNDhRZUpUMHhWNmxLMkJhbFJXYUtvaHRJR0lIZE1tbWdhNUhxbDlMWFBMYVJrdjFvZHhxMVhQWmxzL1NJelRIUFlmTzV4MmFlM2YxTng0aFVZZDdzaWppTHkyRlk9IiwiYXpwIjoiYjM2NGM1NDctMTVmNi00NGM4LWI0MzYtNTI3MmZlYjI0MTdhIiwiYXpwYWNyIjoiMCIsIm5hbWUiOiJUb2JpYXMgSGV5ZXIiLCJvaWQiOiI4MjMyNzk1Yy1hZDk4LTQ4MjMtOTI3MC00NmY4MjRjYzJhMDgiLCJwcmVmZXJyZWRfdXNlcm5hbWUiOiJkaWdpX3RoQGRpZ2kzLmRlIiwicmgiOiIxLkFRc0E4NG5RM1NlaFdFaUdjcTZRdjRMWkJFZkZaTFAyRmNoRXREWlNjdjZ5UVhvU0FUb0xBQS4iLCJzY3AiOiJmaWxldHJhbnNmZXIucmVhZCIsInNpZCI6IjAwNGU5ZDE5LTgyYWYtNTBiYi00NWQzLWRmYmI2YTk3N2M2OSIsInN1YiI6IjVJbkdJLWhjWnNBY0pYY0xvcmpBVTlBNTRCSVZCWXR4NlBwR1JNSG40bWsiLCJ0aWQiOiJkZGQwODlmMy1hMTI3LTQ4NTgtODY3Mi1hZTkwYmY4MmQ5MDQiLCJ1dGkiOiJiS3dtTjRIOTgwbTdyMlM1Y2ljS0FBIiwidmVyIjoiMi4wIiwieG1zX2Z0ZCI6IjJuTzN0d0JsZEc1dDk0eEdNcUU2S3ZZZUR1ampYaEdCazBaVk0wUzFiNElCWlhWeWIzQmxkMlZ6ZEMxa2MyMXoifQ.nEyqGjE22VLQc-35F6D7nGrotU3mVArFUzcKP_J9W4iyFHTSQx3DAo4XHvKPgj7dR2tZnt0ryn0UON6SsijkdiE4MRdEeAq64L2DwDTx0vXZEJIHSreHnCGQzjlqOQPHs5VQ4nPWn0HdyrVeO1bGNVsInkmOj-n58Ady5k6KevQDHVWdmuwSTJWy3HAjop8fRkV9k0IlBoWBR63spVbMUTK-BCQdRII-_m50Tga8jm5CUoapNI2E7jL-bU3LcpktWh7PUjZXJWvWKX7JPH0EnWvwTLqohS75TIfgcO42pCvxSrL3QvXqERgTQBxR4CmgE8XyN-NQMDgUvLmpBkEumQ";
    }
}
